import { CommandType, TurnResult } from '../types';

/**
 * Lv6 達人 AI の相手モデル化（ベイズ推定）
 *
 * 相手のプレイスタイルを 4 つのアーキタイプに分類し、過去の行動から
 * ベイズ推定で各アーキタイプの事後確率を求める。Lv6 はこの推定結果を
 * 使って相手の次の手をより正確に予測し、対応戦略を立てる。
 *
 * - 試合開始時: 各アーキタイプ 25% の事前確率
 * - 観察ごとに: P(A|D) = P(D|A) × P(A) / P(D) で事後確率更新
 *
 * 純粋関数。同じ履歴に対しては常に同じ推定結果を返す。
 */

export type OpponentArchetypeId = 'aggressive' | 'defensive' | 'tactician' | 'burst';

export interface OpponentArchetype {
  id: OpponentArchetypeId;
  name: string;
  /** 各コマンドが選ばれる相対重み（合計が一定でなくてOK、内部で正規化） */
  commandWeights: Partial<Record<CommandType, number>>;
}

/** 4 つのアーキタイプ定義 */
export const ARCHETYPES: OpponentArchetype[] = [
  {
    id: 'aggressive',
    name: 'Aggressive',
    commandWeights: {
      [CommandType.ADVANCE]: 2.0,
      [CommandType.WEAPON_ATTACK]: 2.0,
      [CommandType.SPECIAL_ATTACK]: 1.0,
      [CommandType.RETREAT]: 0.3,
      [CommandType.REFLECTOR]: 0.5,
      [CommandType.STANCE_A]: 1.2,
      [CommandType.STANCE_B]: 0.5,
    },
  },
  {
    id: 'defensive',
    name: 'Defensive',
    commandWeights: {
      [CommandType.ADVANCE]: 0.5,
      [CommandType.WEAPON_ATTACK]: 0.8,
      [CommandType.SPECIAL_ATTACK]: 0.8,
      [CommandType.RETREAT]: 2.0,
      [CommandType.REFLECTOR]: 2.0,
      [CommandType.STANCE_A]: 0.5,
      [CommandType.STANCE_B]: 1.5,
    },
  },
  {
    id: 'tactician',
    name: 'Tactician',
    commandWeights: {
      [CommandType.ADVANCE]: 1.0,
      [CommandType.WEAPON_ATTACK]: 1.0,
      [CommandType.SPECIAL_ATTACK]: 1.0,
      [CommandType.RETREAT]: 1.0,
      [CommandType.REFLECTOR]: 1.0,
      [CommandType.STANCE_A]: 1.8,
      [CommandType.STANCE_B]: 1.8,
    },
  },
  {
    id: 'burst',
    name: 'Burst',
    commandWeights: {
      [CommandType.ADVANCE]: 1.3,
      [CommandType.WEAPON_ATTACK]: 0.8,
      [CommandType.SPECIAL_ATTACK]: 2.5,
      [CommandType.RETREAT]: 0.7,
      [CommandType.REFLECTOR]: 0.6,
      [CommandType.STANCE_A]: 1.0,
      [CommandType.STANCE_B]: 0.8,
    },
  },
];

/** アーキタイプ事後確率の型（合計1） */
export type ArchetypePosterior = Record<OpponentArchetypeId, number>;

/** 一様事前分布 */
export function uniformPrior(): ArchetypePosterior {
  return { aggressive: 0.25, defensive: 0.25, tactician: 0.25, burst: 0.25 };
}

/**
 * アーキタイプ A における コマンド C の尤度 P(C|A) を計算
 */
function commandLikelihood(archetype: OpponentArchetype, command: CommandType): number {
  const weights = archetype.commandWeights;
  const total = Object.values(weights).reduce<number>((sum, w) => sum + (w ?? 0), 0);
  if (total === 0) return 1 / 7; // 防御
  const w = weights[command] ?? 0.5; // 未定義コマンドは中庸
  return w / total;
}

/**
 * 1コマンド観察ごとにベイズ更新
 *
 * @param prior 事前確率
 * @param command 観察されたコマンド
 * @returns 事後確率
 */
function bayesUpdate(prior: ArchetypePosterior, command: CommandType): ArchetypePosterior {
  const numerators: ArchetypePosterior = { aggressive: 0, defensive: 0, tactician: 0, burst: 0 };
  let evidence = 0;
  for (const archetype of ARCHETYPES) {
    const likelihood = commandLikelihood(archetype, command);
    const numer = likelihood * prior[archetype.id];
    numerators[archetype.id] = numer;
    evidence += numer;
  }

  if (evidence === 0) return prior; // 防御

  return {
    aggressive: numerators.aggressive / evidence,
    defensive: numerators.defensive / evidence,
    tactician: numerators.tactician / evidence,
    burst: numerators.burst / evidence,
  };
}

/**
 * ターン履歴から相手のアーキタイプ事後確率を推定
 *
 * @param turnHistory ターン履歴
 * @param opponentPlayerId 推定対象のプレイヤーID
 * @returns アーキタイプ事後確率（合計 1）
 */
export function inferOpponentArchetype(
  turnHistory: TurnResult[],
  opponentPlayerId: 'player1' | 'player2',
): ArchetypePosterior {
  let posterior = uniformPrior();

  for (const turn of turnHistory) {
    const cmds = opponentPlayerId === 'player1' ? turn.player1Commands : turn.player2Commands;
    posterior = bayesUpdate(posterior, cmds.first.type);
    posterior = bayesUpdate(posterior, cmds.second.type);
  }

  return posterior;
}

/**
 * アーキタイプ事後確率から、加重平均された行動傾向ベクトルを計算
 *
 * 各アーキタイプの commandWeights を事後確率で重み付けして合成する。
 * 結果は「相手はこのコマンドを出しそう」というスコアマップ。
 */
export function getArchetypeWeightedCommandBias(
  posterior: ArchetypePosterior,
): Record<CommandType, number> {
  const result: Record<string, number> = {};
  for (const cmd of Object.values(CommandType)) {
    let weighted = 0;
    for (const archetype of ARCHETYPES) {
      const w = archetype.commandWeights[cmd] ?? 1.0;
      weighted += posterior[archetype.id] * w;
    }
    result[cmd] = weighted;
  }
  return result as Record<CommandType, number>;
}

/**
 * 最も尤度の高いアーキタイプを返す（デバッグ・UI 表示用）
 */
export function getMostLikelyArchetype(posterior: ArchetypePosterior): OpponentArchetypeId {
  let best: OpponentArchetypeId = 'tactician';
  let bestProb = -1;
  for (const archetype of ARCHETYPES) {
    if (posterior[archetype.id] > bestProb) {
      bestProb = posterior[archetype.id];
      best = archetype.id;
    }
  }
  return best;
}
